import type { RightPanelPresentation } from "./rightPanelLayout";

/**
 * Making the right panel a column instead of a drawer.
 *
 * Forge's shell puts the app bar above a row that holds the transcript and the
 * right panel, so the bar spanned both and the panel hung underneath it — the
 * "under bar" look. t3code gives each of its three columns its own top strip,
 * which is what produces the vertical divider running from the very top.
 *
 * Rather than restructure the shell, the same result comes from two facts kept
 * in agreement: the panel starts at y=0, and the bar stops where the panel
 * begins. Both are derived here, from one condition, because a disagreement is
 * invisible until you look — the bar would either reserve width for a panel
 * that is not there, or paint over one that is (it is z-20, the panel z-10).
 *
 * Only an *inline* panel is a column. An overlay sheet floats above the content
 * on narrow viewports and must leave the bar alone.
 */
export const RIGHT_PANEL_RESERVED_WIDTH_VAR = "--forge-right-panel-reserved-width";

const BELOW_TOP_BAR = "var(--forge-app-top-bar-height)";

interface RightPanelColumnInput {
  readonly presentation: RightPanelPresentation;
  readonly open: boolean;
}

/**
 * A closed offcanvas panel is still in the DOM, parked off the right edge, so
 * "open" is part of the condition rather than an optimisation.
 */
function isRightPanelColumn(input: RightPanelColumnInput): boolean {
  return input.presentation === "inline" && input.open;
}

export function resolveRightPanelColumnOffsets(input: RightPanelColumnInput): {
  readonly top: string;
} {
  return { top: isRightPanelColumn(input) ? "0px" : BELOW_TOP_BAR };
}

export function resolveTopBarReservedRight(input: RightPanelColumnInput): string {
  return isRightPanelColumn(input) ? `var(${RIGHT_PANEL_RESERVED_WIDTH_VAR}, 0px)` : "0px";
}
